<template>
  <UCard
    :ui="{
      root: 'bg-neutral-900/40 backdrop-blur-lg h-fit',
      header: 'p-0 sm:p-0 overflow-hidden',
    }"
    class="overflow-hidden"
  >
    <UBadge
      v-if="reviewCount > 0"
      color="neutral"
      variant="soft"
      class="absolute top-3 left-3 rounded-full border-2 border-neutral-800/60 bg-neutral-950/60 font-bold shadow-lg backdrop-blur-lg"
    >
      <div
        v-if="reviewCount > 0 && !hideMap"
        class="flex items-center justify-center space-x-2 text-sm text-neutral-400"
      >
        <div>
          <div v-for="i in 5" :key="i" class="inline-flex space-x-1">
            <UIcon
              name="i-heroicons-star-solid"
              class="mt-1 size-4 text-yellow-400"
            />
          </div>
        </div>
        <p>{{ reviewCount }}+</p>
      </div>
    </UBadge>
    <template v-if="longitude && latitude && !hideMap" #header>
      <div class="relative isolate h-64 w-full">
        <USkeleton class="absolute inset-0 h-full w-full rounded-none" />
        <ClientOnly>
          <MapboxMap
            :map-id="`map-${longitude}-${latitude}`"
            class="absolute z-[1] h-full w-full"
            :options="{
              style: 'mapbox://styles/mapbox/dark-v11',
              center: [longitude, latitude],
              zoom: 14,
              scrollZoom: false,
              dragRotate: false,
              dragPan: false,
              failIfMajorPerformanceCaveat: false,
            }"
          >
            <MapboxNavigationControl
              position="top-right"
              :options="{
                showCompass: false,
              }"
            />
            <MapboxDefaultMarker
              :options="{
                color: '#ffcd16',
              }"
              :lnglat="[longitude, latitude]"
              :marker-id="`marker-${longitude}-${latitude}`"
            >
              <template #marker>
                <UButton
                  icon="i-heroicons-map-pin-solid"
                  variant="link"
                  :ui="{
                    leadingIcon: 'size-10 text-yellow-400',
                  }"
                />
              </template>
            </MapboxDefaultMarker>
          </MapboxMap>
        </ClientOnly>
      </div>
    </template>
    <div>
      <h4 class="-mt-2 text-lg font-extrabold">{{ blok.headline }}</h4>
      <p class="-mt-1 mb-4 font-bold text-yellow-400">{{ blok.town }}</p>
      <div
        class="grid grid-cols-2 gap-2 text-sm font-medium lg:grid-cols-1 xl:grid-cols-2"
      >
        <dl v-if="blok.address" class="flex space-x-2">
          <dt class="flex-none">
            <span class="sr-only">Address</span>
            <UIcon name="i-lucide-map" class="size-5 text-yellow-400" />
          </dt>
          <dd class="whitespace-pre-line">
            {{ blok.address }}
          </dd>
        </dl>
        <div class="grid gap-2">
          <dl
            v-if="blok.phone_number"
            :class="[
              blok.address ? 'flex space-x-2' : 'col-span-1 flex space-x-2',
            ]"
          >
            <dt class="flex-none">
              <span class="sr-only">Phone</span>
              <UIcon name="i-lucide-phone" class="size-5 text-yellow-400" />
            </dt>
            <dd class="whitespace-pre-line">
              <NuxtLink
                :to="`tel:${blok.phone_number}`"
                class="hover:underline"
                @click="$clientPosthog?.capture('phone_clicked')"
                >{{ blok.phone_number }}</NuxtLink
              >
            </dd>
          </dl>
          <dl
            v-if="blok.contact_form && blok.contact_form.length > 0"
            :class="[
              blok.address || blok.phone_number
                ? 'flex space-x-2'
                : 'col-span-1 flex space-x-2',
            ]"
          >
            <dt class="flex-none">
              <span class="sr-only">Contact Form</span>
              <UIcon name="i-lucide-mail" class="size-5 text-yellow-400" />
            </dt>
            <dd class="whitespace-pre-line">
              <UModal
                v-model:open="modalState"
                title="Get In Touch"
                description="We'd love to hear from you!"
                :close="{
                  color: 'primary',
                  variant: 'outline',
                  class: 'rounded-full',
                }"
                :ui="{
                  overlay: 'bg-neutral-950/60	backdrop-blur-sm',
                  content: 'bg-neutral-950/60 backdrop-blur-xl overflow-y-auto',
                }"
              >
                <button
                  class="cursor-pointer hover:underline"
                  @click="$clientPosthog?.capture('email_clicked')"
                >
                  Email Our Team
                </button>

                <template #body>
                  <LazyMarketingFormEnquiry
                    :blok="blok"
                    :form="blok.contact_form[0]"
                    type="location"
                    @submitted="closeModal"
                  />
                </template>
              </UModal>
            </dd>
          </dl>
          <dl
            v-if="blok.what_three_words"
            :class="[
              blok.address ? 'flex space-x-2' : 'col-span-1 flex space-x-2',
            ]"
          >
            <dt class="-ml-1 flex-none -rotate-12 -space-x-2.5">
              <span class="sr-only">What Three Words</span>
              <UIcon name="i-lucide-slash" class="size-4 text-red-500" />
              <UIcon name="i-lucide-slash" class="size-4 text-red-500" />
              <UIcon name="i-lucide-slash" class="size-4 text-red-500" />
            </dt>
            <dd
              class="w-full overflow-hidden text-ellipsis whitespace-pre-line"
            >
              <NuxtLink
                :to="`https://what3words.com/${blok.what_three_words}`"
                target="_blank"
                class="hover:underline"
                @click="$clientPosthog?.capture('what_three_words_clicked')"
              >
                {{ blok.what_three_words }}
              </NuxtLink>
            </dd>
          </dl>
        </div>
      </div>
    </div>
    <template
      v-if="
        activitiesCount > 0 ||
        giftsCount > 0 ||
        eventsCount > 0 ||
        blok.Amenities
      "
      #footer
    >
      <p class="mb-4 text-sm font-bold text-neutral-400 uppercase">Amenities</p>
      <div class="mx-auto flex flex-wrap items-center gap-y-2">
        <UChip
          v-if="activitiesCount > 0"
          class="font-mono"
          size="3xl"
          :text="activitiesCount"
          :ui="{
            root: 'p-0.5',
            base: 'p-0.5 rounded-full font-bold mr-4',
          }"
        >
          <UTooltip
            :text="`${activitiesCount} ${activitiesCount === 1 ? 'Activity' : 'Activities'}`"
          >
            <UIcon name="i-lucide-zap" class="mr-4 size-5 text-yellow-400" />
          </UTooltip>
        </UChip>
        <UChip
          v-if="giftsCount > 0"
          class="font-mono"
          size="3xl"
          :text="giftsCount"
          :ui="{
            root: 'p-0.5',
            base: 'p-0.5 rounded-full font-bold mr-4',
          }"
        >
          <UTooltip
            :text="`${giftsCount} ${giftsCount === 1 ? 'Gift Voucher' : 'Gift Vouchers'}`"
          >
            <UIcon name="i-lucide-gift" class="mr-4 size-5 text-yellow-400" />
          </UTooltip>
        </UChip>
        <UChip
          v-if="eventsCount > 0"
          class="font-mono"
          size="3xl"
          :text="eventsCount"
          :ui="{
            root: 'p-0.5',
            base: 'p-0.5 rounded-full font-bold mr-4',
          }"
        >
          <UTooltip
            :text="`${eventsCount} ${eventsCount === 1 ? 'Event' : 'Events'}`"
          >
            <UIcon
              name="i-lucide-calendar-days"
              class="mr-4 size-5 text-yellow-400"
            />
          </UTooltip>
        </UChip>
        <UTooltip v-for="item in blok.Amenities" :key="item" :text="item">
          <UIcon
            :name="
              item === 'Toilets'
                ? 'i-lucide-toilet'
                : item === 'Showers'
                  ? 'i-lucide-shower-head'
                  : item === 'Parking'
                    ? 'i-lucide-circle-parking'
                    : item === 'Changing Rooms'
                      ? 'i-lucide-shirt'
                      : item === 'Lockers'
                        ? 'i-lucide-vault'
                        : item === 'Wifi'
                          ? 'i-lucide-wifi'
                          : item === 'Shop'
                            ? 'i-lucide-shopping-basket'
                            : item === 'Cafe'
                              ? 'i-lucide-coffee'
                              : item === 'Disabled Access'
                                ? 'i-lucide-accessibility'
                                : item === 'Castle'
                                  ? 'i-lucide-castle'
                                  : item === 'Accommodation'
                                    ? 'i-lucide-bed'
                                    : item === 'Spa'
                                      ? 'i-lucide-heart-handshake'
                                      : item === 'Woodland'
                                        ? 'i-lucide-tree-pine'
                                        : item === 'Lake'
                                          ? 'i-lucide-waves'
                                          : ''
            "
            class="mr-4 size-5 text-yellow-400"
          />
        </UTooltip>
      </div>
    </template>
  </UCard>
</template>

<script setup lang="ts">
import type {
  LocationStoryblok,
  ContentContactDetailsStoryblok,
} from '~/types/component-types-sb.d.ts';

interface Props {
  blok: LocationStoryblok | ContentContactDetailsStoryblok;
  type?: string;
  activitiesCount?: number;
  giftsCount?: number;
  eventsCount?: number;
  hideMap?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: '',
  activitiesCount: 0,
  giftsCount: 0,
  eventsCount: 0,
  hideMap: false,
});

const modalState = ref(false);

const closeModal = () => {
  modalState.value = false;
};

// Type conversion helpers
const longitude = computed(() => Number(props.blok.longitude));
const latitude = computed(() => Number(props.blok.latitude));
const reviewCount = computed(() => Number(props.blok.review_count));
</script>
